import api from './client'

// Credits are what users see; currency appears only on checkout surfaces
// (plan cards, the buy-confirm modal) and on invoices.

export interface CreditsSummary {
  balance: number
  user_balance: number
  /** Which wallet pays for this user's platform-key AI: own, their tenant's, or none. */
  scope: 'user' | 'tenant' | 'own'
  plan_code: string
  plan_label: string
  /** Whether this plan includes the runtime-package download. */
  allow_runtime_download: boolean
  /** The plan a user must reach for downloads — used to label the lock hint. */
  download_plan_label: string
  credit_billing_enabled: boolean
  low_balance_threshold: number | null
  /** Credits per USD. Lets any surface that only knows a provider cost render
   *  it in credits — users are never shown dollars. */
  credits_per_usd: number
  /** Today's agent turns against the plan's daily allowances (UTC day). */
  agent_turns?: AgentTurnsStatus
}

export interface AgentTurnAllowance {
  /** null = unlimited on this plan. */
  limit: number | null
  used: number
  remaining: number | null
}

export interface AgentTurnsStatus {
  /** Turns on Twasta AI, paid in credits. */
  credit: AgentTurnAllowance
  /** Turns on the user's own key / subscription / a free model. */
  own: AgentTurnAllowance
  /** The allowance the user's latest turn today drew on (credit when none). */
  current: 'credit' | 'own'
  /** At least one allowance is limited on this plan. */
  capped: boolean
  resets_at: string
}

export interface SubscriptionStatus {
  plan_code: string
  plan_label: string
  status: string | null
  billing_period: string | null
  auto_renew: boolean | null
  cancel_at_period_end: boolean
  current_period_end: string | null
  usable: boolean
  /** Set by the auto-renew toggle when nothing can actually charge a renewal
   *  (no live gateway mandate) — the UI should offer "Renew now" to set one up. */
  requires_checkout?: boolean
  detail?: string
}

export interface LedgerEntry {
  id: number
  created_at: string | null
  entry_type: string
  amount: number
  balance_after: number
  reference_type: string | null
  reference_id: string | null
  description: string | null
}

export interface CatalogPlan {
  id: number | null
  code: string
  display_name: string
  description: string | null
  included_monthly_credits: number
  trial_credits: number
  max_applications: number | null
  max_users_per_tenant: number | null
  max_tables_per_project: number | null
  max_screens_per_project: number | null
  max_records_per_project: number | null
  /** Ask-AI / Build turns per UTC day on own AI / on AI credits; null = unlimited. */
  max_own_ai_turns_per_day?: number | null
  max_credit_turns_per_day?: number | null
  allow_runtime_download: boolean
  download_fee_usd_minor: number
  download_fee_inr_minor: number
  grace_days: number
  is_active: boolean
  is_public: boolean
  sort_order: number
  is_current: boolean
  prices: Record<string, { amount_minor: number; currency: string; display: string; has_provider_plan: boolean }>
}

export interface CatalogPack {
  code: string
  name: string
  credits: number
  bonus_credits: number
  total_credits: number
  amount_minor: number
  currency: string
  display: string
}

export interface Catalog {
  currency: string
  country: string | null
  plans: CatalogPlan[]
  packs: CatalogPack[]
  subscription: SubscriptionStatus
  balance: CreditsSummary
  provider: { provider: string | null; key_id?: string; enabled: boolean }
  credit_billing_enabled: boolean
}

export interface CheckoutOrder {
  order: {
    id: string
    purpose: string
    total_minor: number
    currency: string
    display_total: string
    provider_order_id: string | null
    provider_subscription_id: string | null
    status: string
  }
  quote: Record<string, unknown>
  checkout: { mode: 'order' | 'subscription'; order_id?: string; subscription_id?: string }
  provider_config: { provider: string; key_id?: string; enabled: boolean }
}

export interface Invoice {
  invoice_number: string
  issued_at: string | null
  currency: string
  subtotal_minor: number
  discount_minor: number
  tax_minor: number
  total_minor: number
  tax_breakdown: Array<{ name: string; rate: number; amount_minor: number }>
  line_items: Array<{ description: string; credits?: number | null; amount_minor: number }>
  status: string
  tax_id: string | null
  display: { subtotal: string; discount: string; tax: string; total: string }
}

export async function getBalance(): Promise<CreditsSummary> {
  const res = await api.get('/billing/me/balance')
  return res.data
}

export async function getWallet(params?: { limit?: number; offset?: number }) {
  const res = await api.get('/billing/me/wallet', { params })
  return res.data as {
    wallet: { id: number | null; balance: number }
    summary: CreditsSummary
    subscription: SubscriptionStatus
    total: number
    entries: LedgerEntry[]
  }
}

export async function getCatalog(): Promise<Catalog> {
  const res = await api.get('/billing/catalog')
  return res.data
}

export async function createOrder(body: {
  purpose: string
  target_ref?: string
  billing_period?: string
  coupon_code?: string
  project_ref?: string
}): Promise<CheckoutOrder> {
  const res = await api.post('/billing/checkout/order', body)
  return res.data
}

export async function verifyOrder(body: {
  order_id: string
  provider_payment_id?: string
  provider_order_id?: string
  provider_subscription_id?: string
  signature?: string
}) {
  const res = await api.post('/billing/checkout/verify', body)
  return res.data
}

export async function validateCoupon(code: string, scope = 'packs', amount_minor = 0) {
  const res = await api.post('/billing/coupons/validate', { code, scope, amount_minor })
  return res.data as { valid: boolean; error?: string; code?: string; description?: string }
}

export async function listInvoices(): Promise<{ invoices: Invoice[] }> {
  const res = await api.get('/billing/invoices')
  return res.data
}

/** Printable invoice URL — opened in a new tab, printed to PDF from there. */
export function invoiceDocumentUrl(number: string): string {
  return `${api.defaults.baseURL}/billing/invoices/${encodeURIComponent(number)}/pdf`
}

export interface BillingProfile {
  country: string | null
  tax_id: string | null
  billing_name: string | null
  billing_state: string | null
  currency: string
}

export async function getBillingProfile(): Promise<BillingProfile> {
  const res = await api.get('/billing/me/billing-profile')
  return res.data
}

export async function updateBillingProfile(body: Partial<BillingProfile>): Promise<BillingProfile> {
  const res = await api.put('/billing/me/billing-profile', body)
  return res.data
}

export async function getSubscription(): Promise<SubscriptionStatus> {
  const res = await api.get('/billing/me/subscription')
  return res.data
}

export async function setAutoRenew(auto_renew: boolean): Promise<SubscriptionStatus> {
  const res = await api.put('/billing/me/subscription/auto-renew', { auto_renew })
  return res.data
}

/** Renew the current plan NOW — returns a checkout order for the gateway. */
export async function renewSubscription(coupon_code?: string): Promise<CheckoutOrder> {
  const res = await api.post('/billing/me/subscription/renew', null, {
    params: coupon_code ? { coupon_code } : undefined,
  })
  return res.data
}

export async function cancelSubscription(): Promise<SubscriptionStatus> {
  const res = await api.post('/billing/me/subscription/cancel')
  return res.data
}

export interface DownloadEligibility {
  allowed: boolean
  /** Money fee (minor units) charged to the APPLICATION's wallet. */
  fee_minor: number
  currency: string
  fee_display: string
  balance_minor: number
  balance_display: string
  sufficient: boolean
  plan_code: string
  plan_label: string
  required_plan_label: string
}

export async function getDownloadEligibility(project?: string): Promise<DownloadEligibility> {
  const res = await api.get('/billing/me/download-eligibility', {
    params: project ? { project } : undefined,
  })
  return res.data
}

// ── Tenant wallet (organization admin) ────────────────────────────────────

export async function getTenantWallet(params?: { limit?: number; offset?: number }) {
  const res = await api.get('/billing/tenant/wallet', { params })
  return res.data as {
    wallet: { id: number | null; balance: number }
    total: number
    entries: LedgerEntry[]
    member_usage: Array<{ user_id: string; user_name: string; credits: number; calls: number }>
  }
}

export async function tenantTopup(pack_code: string, coupon_code?: string): Promise<CheckoutOrder> {
  const res = await api.post('/billing/tenant/topup', { pack_code, coupon_code })
  return res.data
}

// ── Project wallet (project manager) ──────────────────────────────────────

export interface ProjectWallet {
  id: number | null
  balance: number
  /** NULL = a credits wallet; a code = MONEY in that currency's minor units. */
  currency: string | null
  display: string | null
}

export interface ProjectBillingConfig {
  owner_user_id: string
  project_id: string
  /** The currency this application bills in (its owner's billing currency). */
  currency: string
  // Per-seat / fee overrides in minor units. null = inherit / free.
  runtime_user_cost_usd_minor: number | null
  runtime_user_cost_inr_minor: number | null
  pro_invite_cost_usd_minor: number | null
  pro_invite_cost_inr_minor: number | null
  self_reg_open_to_pro: boolean
  self_reg_fee_regular_usd_minor: number | null
  self_reg_fee_regular_inr_minor: number | null
  self_reg_fee_pro_usd_minor: number | null
  self_reg_fee_pro_inr_minor: number | null
  effective_runtime_user_cost_minor: number
  effective_pro_invite_cost_minor: number
  effective_runtime_user_cost_display: string
  effective_pro_invite_cost_display: string
  defaults: Record<string, number>
  wallet: ProjectWallet
  credit_billing_enabled: boolean
}

export async function getProjectBillingConfig(projectId: string): Promise<ProjectBillingConfig> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/config`)
  return res.data
}

export async function updateProjectBillingConfig(
  projectId: string,
  body: Partial<ProjectBillingConfig>,
): Promise<ProjectBillingConfig> {
  const res = await api.put(`/projects/${encodeURIComponent(projectId)}/billing/config`, body)
  return res.data
}

export async function getProjectWallet(projectId: string, params?: { limit?: number; offset?: number }) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/wallet`, { params })
  return res.data as { wallet: ProjectWallet; total: number; entries: LedgerEntry[] }
}

export async function topupProjectWallet(
  projectId: string, amount_minor: number, coupon_code?: string,
): Promise<CheckoutOrder> {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/billing/topup`, {
    amount_minor, coupon_code,
  })
  return res.data
}

export async function getProjectCharges(projectId: string) {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/charges`)
  return res.data as {
    total: number
    charges: Array<{
      id: number; subject_type: string; subject_id: string; charge_type: string
      amount_minor: number | null; currency: string | null; display: string | null
      charged_by: string | null; created_at: string | null
    }>
  }
}

export interface InviteCost {
  amount_minor: number
  currency: string
  display: string
  already_charged: boolean
  invitee_is_pro: boolean
  balance_minor: number
  balance_display: string
  sufficient: boolean
}

export async function getInviteCost(projectId: string, email?: string): Promise<InviteCost> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/invite-cost`, {
    params: email ? { email } : undefined,
  })
  return res.data
}

/** Money in minor units -> a display string ("₹1,200.00", "$5.00"). */
export function formatMoney(amountMinor: number | null | undefined, currency: string | null | undefined): string {
  if (amountMinor === null || amountMinor === undefined) return '—'
  const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : (currency ? `${currency} ` : '')
  return `${symbol}${(amountMinor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Credits are shown to 0 dp above 100, 2 dp below — small balances matter. */
export function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const abs = Math.abs(value)
  if (abs >= 100) return Math.round(value).toLocaleString()
  return value.toFixed(2).replace(/\.00$/, '')
}

// ── Resource billing (cloud cost + owner margin) ────────────────────────────
// docs/runtime-pricing-cloud-cost-design.md

export type MeterKey = 'users' | 'rows' | 'db_gb' | 'file_gb' | 'requests' | 'base_fee'

export interface UsageLine {
  meter: string
  label: string
  qty: number
  allowance: number | null
  over: number
  units: number
  rate_minor: number
  amount_minor: number
  action: 'charge' | 'block' | 'alert'
  over_allowance?: boolean
}

export interface RateCardRow {
  meter: string
  label: string
  allowance_qty: number | null
  unit_block: number
  rate_usd_minor: number
  rate_inr_minor: number
  over_action: 'charge' | 'block' | 'alert'
  enabled: boolean
  configured: boolean
}

export interface ResourceUsage {
  enabled: boolean
  yyyymm: string
  plan_code: string
  currency: string
  lines: UsageLine[]
  total_minor: number
  total_display: string
  rate_card: Record<string, RateCardRow>
}

export interface SheetMeterRow {
  meter: string
  label: string
  allowance_qty: number
  unit_block: number
  rate: number
  cost_minor?: number
}

export interface BelowCostWarning {
  meter: string
  label: string
  your_rate_minor: number
  platform_rate_minor: number
  currency: string
}

export interface PriceSheet {
  id: number
  version: number
  mode: 'free' | 'per_user' | 'resource'
  currency: string
  per_user_minor: number | null
  signup_fee_minor: number | null
  published_by: string | null
  published_at: string | null
  meters: Record<string, SheetMeterRow>
  warnings?: BelowCostWarning[]
}

export interface PriceSheetInfo {
  enabled: boolean
  sheet: PriceSheet | null
  defaults: Record<string, SheetMeterRow>
  currency: string
  marketplace: MarketplaceSettings
}

export interface MarketplaceSettings {
  resource_billing_enabled: boolean
  resource_billing_observe: boolean
  platform_fee_percent: number
  default_margin_percent: number
  owner_rate_ceiling_multiplier: number
  price_change_notice_days: number
}

export interface EarningsOrg {
  grant_id: number
  tenant_id: string
  usage: Record<string, number>
  billed_minor: number
  platform_fee_minor: number
  net_minor: number
  billed_display?: string
  net_display?: string
  mode: string
  sheet_version: number | null
  currency: string
}

export interface Earnings {
  yyyymm: string
  organizations: EarningsOrg[]
  net_earnings_minor: number
  cloud_cost_minor: number
  margin_minor: number
  currency: string
  cloud_cost_display?: string
  net_earnings_display?: string
  margin_display?: string
  own_org_usage: Record<string, number>
}

export interface PublishSheetBody {
  mode: 'free' | 'per_user' | 'resource'
  per_user_minor?: number | null
  signup_fee_minor?: number | null
  meters?: Record<string, { rate: number; unit_block?: number; allowance_qty?: number }>
  confirm_below_cost?: boolean
}

export interface PublishSheetResult {
  published: boolean
  requires_confirmation?: boolean
  warnings: BelowCostWarning[]
  sheet?: PriceSheet
}

export async function getResourceUsage(projectId: string): Promise<ResourceUsage> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/resource-usage`)
  return res.data
}

export async function getPriceSheet(projectId: string): Promise<PriceSheetInfo> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/price-sheet`)
  return res.data
}

export async function publishPriceSheet(
  projectId: string, body: PublishSheetBody,
): Promise<PublishSheetResult> {
  const res = await api.post(`/projects/${encodeURIComponent(projectId)}/billing/price-sheet`, body)
  return res.data
}

export async function getEarnings(projectId: string, yyyymm?: string): Promise<Earnings> {
  const res = await api.get(`/projects/${encodeURIComponent(projectId)}/billing/earnings`, {
    params: yyyymm ? { yyyymm } : undefined,
  })
  return res.data
}

// ── Consuming-organization side ─────────────────────────────────────────────

export interface OrgSubscription {
  grant_id: number
  project_id: string
  owner_user_id: string
  provider: string
  mode: string
  sheet_version: number | null
  usage: Record<string, number>
  month_to_date_minor: number
  month_to_date_display: string
  currency: string
}

export interface OrgWalletSummary {
  id: number | null
  balance: number
  currency: string | null
  display: string | null
  upcoming_minor: number
  upcoming_display: string
  sufficient: boolean
}

export interface OrgSubscriptions {
  enabled: boolean
  yyyymm: string
  subscriptions: OrgSubscription[]
  wallets: OrgWalletSummary[]
}

export async function getOrgSubscriptions(): Promise<OrgSubscriptions> {
  const res = await api.get('/org/billing/subscriptions')
  return res.data
}

export async function getOrgWallet(currency: string, params?: { limit?: number; offset?: number }) {
  const res = await api.get('/org/billing/wallet', { params: { currency, ...params } })
  return res.data
}

export async function topupOrgWallet(
  amount_minor: number, currency: string, coupon_code?: string,
): Promise<CheckoutOrder> {
  const res = await api.post('/org/billing/topup', { amount_minor, currency, coupon_code })
  return res.data
}

export async function getGrantPriceSheet(grantId: number) {
  const res = await api.get(`/org/billing/subscriptions/${grantId}/price-sheet`)
  return res.data
}

export async function acceptGrantSheet(grantId: number) {
  const res = await api.post(`/org/billing/subscriptions/${grantId}/accept-sheet`)
  return res.data
}

/** Meter label + compact usage string for a subscription row. */
export function formatUsage(usage: Record<string, number> | undefined): string {
  if (!usage) return '—'
  const parts: string[] = []
  const n = (v: number) => v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))
  if (usage.users) parts.push(`${Math.round(usage.users)} users`)
  if (usage.rows) parts.push(`${n(usage.rows)} rows`)
  if (usage.db_gb) parts.push(`${usage.db_gb.toFixed(1)} GB`)
  if (usage.file_gb) parts.push(`${usage.file_gb.toFixed(1)} GB files`)
  if (usage.requests) parts.push(`${n(usage.requests)} req`)
  return parts.length ? parts.join(' · ') : '—'
}

export interface OrgInvoice extends Invoice {
  kind?: string
  settlement_ref?: string | null
  can_open_document?: boolean
}

export async function getOrgInvoices(): Promise<{ invoices: OrgInvoice[] }> {
  const res = await api.get('/org/billing/invoices')
  return res.data
}
